import type { TableAutoplayNotice } from '@qlashgeminiz/qgammon-board-ui';
import { AUTOPLAY_REFUSAL_CODES } from '@qlashgeminiz/qgammon-types';

/**
 * L'AUTOPLAY DEL BACKGAMMON, la parte pura (ADR-0234, lotto 2). Il server decide tutto (`autoplayAmmesso`, il tetto, il
 * throttle): qui non si ripete NESSUNA regola, si decide soltanto cosa DISEGNARE a partire da cio' che il server ha detto.
 *
 * Il flag e' un parametro e non una lettura di `env` perche' ogni funzione va provata a flag acceso e a flag spento
 * (`tests/backgammon-autoplay.test.ts`): una funzione che legge `env` da sola si prova solo in un modo.
 */

/** Il posto di un giocatore al tavolo. */
export type PostoAutoplay = 'W' | 'B';

/**
 * L'interruttore nel modale della partita (ADR-0234 §7): SOLO se il flag della TMA e' acceso E il server ha detto, per il MIO posto,
 * che questa partita ammette l'autoplay. `autoplayAllowed` assente o falso e' «non si mostra»: mai un interruttore che il server
 * rifiuterebbe con `autoplay_not_allowed`. Non si mostra nemmeno a partita finita (`match_ended`). Il tetto di concorrenza
 * (`autoplay_busy`) NON e' nello snapshot: si scopre cliccando, ed e' un rifiuto con la sua frase.
 */
export function interruttoreAutoplayVisibile(args: { flag: boolean; consentito: boolean; finita: boolean }): boolean {
  return args.flag && args.consentito && !args.finita;
}

/** Lo stato dell'autoplay come lo dice l'hook di board-ui, letto con i ripieghi di un socket che non lo manda. */
export interface StatoAutoplay {
  autoplay: Record<PostoAutoplay, boolean>;
  consentito: boolean;
  conAtti: boolean;
}

/**
 * Lo legge DIFENSIVAMENTE: un hook piu' vecchio di board-ui 0.16.0 (o un socket finto di un banco) non ha questi campi, e un campo assente
 * vale «spento / non consentito / senza atti» — mai un interruttore o un avviso per una cosa che il server non ha detto. I valori diversi da
 * `true` sono `false`: il tipo dell'hook dice `boolean`, ma un campo che arriva `null` o `undefined` (snapshot di un server precedente) non e' un si'.
 */
export function statoAutoplayDa(sock: {
  autoplay?: Partial<Record<PostoAutoplay, boolean>> | null;
  autoplayAllowed?: boolean | null;
  withAutoplay?: boolean | null;
}): StatoAutoplay {
  return {
    autoplay: { W: sock.autoplay?.W === true, B: sock.autoplay?.B === true },
    consentito: sock.autoplayAllowed === true,
    conAtti: sock.withAutoplay === true,
  };
}

/** Il badge «Autoplay» sul banner di un posto: flag acceso E il server dice che QUEL posto e' pilotato dal bot. */
export function badgeAutoplay(flag: boolean, autoplay: Record<PostoAutoplay, boolean>, posto: PostoAutoplay): boolean {
  return flag && autoplay[posto] === true;
}

/**
 * Il testo prima di confermare la resa del MATCH (ADR-0234 §4(e)): «la partita non conterà per te: c'è l'autoplay».
 * `withAutoplay` e' un fatto del MATCH («almeno un atto deciso in autoplay», non torna piu' falso) che il server manda solo ai
 * giocatori; l'avversario di un posto pilotato che lascia con la resa del match esce dai propri numeri, ed e' questo che deve
 * sapere PRIMA del secondo tocco. Vale uguale per chi l'autoplay l'ha acceso (la partita e' gia' fuori dai suoi numeri): la frase resta vera.
 */
export function avvisoPrimaDellaResa(flag: boolean, withAutoplay: boolean): boolean {
  return flag && withAutoplay;
}

/** I sei rifiuti con nome di ADR-0234 §7. Il resto (un codice nuovo di un server piu' recente) ha il suo ripiego. */
const RIFIUTI_NOTI: readonly string[] = AUTOPLAY_REFUSAL_CODES;

/**
 * La chiave (namespace `backgammon`) della frase per un rifiuto dell'autoplay. Mai il `message` nudo del server: e' inglese e in tono
 * di guasto, dove invece quasi tutti questi rifiuti sono il comportamento previsto (la partita e' classificata, c'e' un tetto, hai
 * toccato troppo in fretta). Un codice che questa build non conosce NON ripiega su un silenzio: ha una frase generica.
 *
 * `NOT_CONNECTED`, `NO_ANSWER` e `BAD_ANSWER` sono i tre codici propri dell'hook di board-ui (il comando non e' partito, non ha avuto
 * risposta, la risposta non si legge): hanno la frase della pausa, che dice la stessa cosa.
 */
export function chiaveRifiutoAutoplay(codice: string): string {
  if (RIFIUTI_NOTI.includes(codice)) return `match.autoplay.rifiuto.${codice}`;
  if (codice === 'NOT_CONNECTED' || codice === 'NO_ANSWER' || codice === 'BAD_ANSWER') return `match.autoplay.rifiuto.trasporto`;
  return 'match.autoplay.rifiuto.altro';
}

/**
 * Un rifiuto arrivato dall'evento `error` (non dall'ack), e cosa ne fa la RIGA D'ERRORE del tavolo.
 *
 * 🔴 Il server rifiuta `autoplay:set` DUE volte: con l'ack (`ackError`) e con l'evento `error` (`emitError`, gameserver `handlers.ts:896-897` @6705778f), e
 * board-ui mette quell'`error` nella riga del tavolo per 4 s. Dei due, il rifiuto lo dice l'INTERRUTTORE (dall'ack, con la sua frase): la riga del tavolo
 * mostrava il `message` inglese grezzo («One autoplay change every 10 seconds»). Rilievo R1 di qgammon-orc-3-review su #720, provato rosso.
 *  · `autoplay_on` (un comando di GIOCO di un posto pilotato) e' l'unico che la riga dice, con la SUA frase: l'interruttore non c'entra, non c'e' un ack da leggere;
 *  · gli altri cinque (`autoplay_not_allowed`, `autoplay_busy`, `autoplay_throttled`, `not_participant`, `match_ended`) TACCIONO: l'ha gia' detto l'interruttore
 *    dall'ack (come `erroreDellaPausa` con PAUSE_NOT_ALLOWED);
 *  · `null` = non e' un codice dell'autoplay, la riga d'errore fa quello che faceva.
 */
export function erroreDiGiocoAutoplay(
  error: { code: string } | null,
): { tipo: 'frase'; chiave: string } | { tipo: 'taci' } | null {
  if (!error) return null;
  if (error.code === 'autoplay_on') return { tipo: 'frase', chiave: chiaveRifiutoAutoplay('autoplay_on') };
  if (RIFIUTI_NOTI.includes(error.code)) return { tipo: 'taci' };
  return null;
}

/** Cosa dice l'avviso al tavolo. `seat` e' il posto che e' cambiato: chi monta lo traduce in un nome. */
export interface AvvisoAlTavolo {
  tipo: 'acceso' | 'spento' | 'botErrore' | 'botErroreMio';
  seat: PostoAutoplay;
}

/**
 * L'AVVISO AL TAVOLO a ogni cambio dell'autoplay di un posto (ADR-0234 §4(a)), da `lastAutoplayNotice` di board-ui.
 *
 * La regola e' quella del tavolo, scritta nei tipi: si scarta `from === mio posto`. Scarta l'eco del MIO gesto (e dei miei altri
 * schermi): l'interruttore gia' lo dice. Tiene un `bot_error` sul mio posto, il cui `from` e' `"server"`: e' l'avviso che il
 * giocatore DEVE vedere («il bot si e' fermato, tocca a te»). Chi guarda e basta (`mioPosto === null`, lo spettatore) vede tutto.
 */
export function avvisoAlTavolo(
  flag: boolean,
  avviso: Pick<TableAutoplayNotice, 'from' | 'seat' | 'on' | 'causa'> | null,
  mioPosto: PostoAutoplay | null,
): AvvisoAlTavolo | null {
  if (!flag || !avviso) return null;
  if (mioPosto !== null && avviso.from === mioPosto) return null;
  if (avviso.causa === 'bot_error') {
    return { tipo: avviso.seat === mioPosto ? 'botErroreMio' : 'botErrore', seat: avviso.seat };
  }
  return { tipo: avviso.on ? 'acceso' : 'spento', seat: avviso.seat };
}

/** Per quanto resta a schermo l'avviso al tavolo. */
export const AVVISO_AUTOPLAY_MS = 8000;
